import { useEffect, useState, useMemo } from "react";
import { api, mesISOLocal } from "@/lib/api";
import PeriodoSelector, { periodoInicial, rangoDePeriodo } from "@/components/PeriodoSelector";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  DollarSign, TrendingUp, TrendingDown, Percent, Wallet, Package,
  ShoppingCart, Award, Pencil, Info, Sparkles, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import KpiCard from "@/components/KpiCard";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";
import UsageTip from "@/components/UsageTip";
import ExcelExportButton from "@/components/ExcelExportButton";
import RevisionCostosPanel from "@/components/RevisionCostosPanel";

/**
 * Rentabilidad — Panel del director con la utilidad neta real:
 *   Ventas − COGS − Comisiones − Otros gastos = Utilidad neta
 * Ofrece: KPIs del periodo, gráfico de utilidad mensual, y tabla por vendedor.
 * Los "otros gastos" (nómina, arriendo, transporte, servicios) se registran
 * mes a mes con un input rápido.
 */



const formatCOP = (n) => `$${Math.round(n || 0).toLocaleString("es-CO")}`;
const formatMesLabel = (mes) => {
  const [y, m] = (mes || "").split("-");
  const nombres = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  const mi = parseInt(m, 10) - 1;
  return nombres[mi] ? `${nombres[mi]} ${y.slice(2)}` : mes;
};

export default function RentabilidadPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [periodo, setPeriodo] = useState(periodoInicial);
  const [modo, setModo] = useState("causacion"); // "causacion" | "caja"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gastoOpen, setGastoOpen] = useState(false);
  const [gastoEdit, setGastoEdit] = useState(null);
  const [gastosList, setGastosList] = useState([]);

  const rangoEfectivo = () => rangoDePeriodo(periodo);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const r = rangoEfectivo();
      if (r.desde) params.set("desde", r.desde);
      if (r.hasta) params.set("hasta", r.hasta);
      params.set("meses", "6");
      params.set("modo", modo);
      const { data } = await api.get(`/reportes/rentabilidad?${params}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la rentabilidad");
    } finally { setLoading(false); }
  };

  const loadGastos = async () => {
    try {
      const { data } = await api.get(`/reportes/gastos-mensuales`);
      setGastosList(data || []);
    } catch {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [periodo, modo]);
  useEffect(() => { loadGastos(); }, []);

  const kpis = data?.kpis || {};
  const serie = data?.serie_mensual || [];
  const vendedores = data?.por_vendedor || [];
  const mesesEnRango = data?.meses_en_rango || [];
  const gastosDelPeriodo = data?.gastos_detalle || [];

  // Comparativo mes actual vs mes anterior (para el header)
  const cmp = useMemo(() => {
    if (serie.length < 2) return null;
    const cur = serie[serie.length - 1];
    const prev = serie[serie.length - 2];
    if (!cur || !prev) return null;
    const delta = cur.utilidad_neta - prev.utilidad_neta;
    const pct = prev.utilidad_neta !== 0 ? (delta / Math.abs(prev.utilidad_neta)) * 100 : null;
    return { cur, prev, delta, pct };
  }, [serie]);

  if (!isAdmin) {
    return <div className="p-10 text-center text-muted-foreground">Solo directores.</div>;
  }

  const abrirEditGasto = (mes) => {
    const existente = gastosList.find((g) => g.mes === mes) || { mes, monto: 0, notas: "" };
    setGastoEdit(existente);
    setGastoOpen(true);
  };

  return (
    <div>
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
            Vista privada · Director
          </div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2 flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-primary" /> Rentabilidad
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Cuánto ganaste realmente después de descontar costo de mercancía, comisiones y otros gastos.
          </p>
        </div>
      </header>

      <UsageTip storageKey="rentabilidad-v2" steps={[
        "La <b>utilidad neta</b> = Ventas − Costo de mercancía (COGS) − Comisiones − Otros gastos.",
        "El <b>COGS</b> usa el costo real congelado en cada venta (incluye landed cost del contenedor).",
        "Cambia entre <b>📒 Causación</b> (todos los pedidos confirmados) y <b>💵 Caja</b> (solo pedidos ya cobrados) según lo que quieras ver.",
        "Los <b>otros gastos</b> (nómina, arriendo, servicios…) se registran mes a mes con el botón <b>Editar</b>.",
      ]} />

      {/* Filtros: periodo unificado + modo + export */}
      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <PeriodoSelector value={periodo} onChange={setPeriodo} testid="rango" />
        <div className="flex items-center gap-2 bg-card border border-border rounded-lg p-1">
          <button
            onClick={() => setModo("causacion")}
            data-testid="modo-causacion"
            title="Incluye todos los pedidos confirmados aunque el cliente aún no haya pagado. Es tu utilidad real del negocio hecho."
            className={`px-3 h-9 rounded-md text-sm font-semibold transition-colors ${modo === "causacion" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
          >
            📒 Causación
          </button>
          <button
            onClick={() => setModo("caja")}
            data-testid="modo-caja"
            title="Solo incluye pedidos que el cliente ya pagó al 100%. Muestra la utilidad que ya entró a caja."
            className={`px-3 h-9 rounded-md text-sm font-semibold transition-colors ${modo === "caja" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
          >
            💵 Caja
          </button>
        </div>
        <div
          className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${modo === "caja" ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-800" : "bg-blue-500/10 border-blue-500/40 text-blue-800"}`}
          data-testid="modo-tag"
          title={modo === "caja"
            ? "Cifras solo de pedidos cobrados al 100%"
            : "Cifras de todos los pedidos confirmados, incluidos los que aún tienen saldo"}
        >
          <Info className="w-3 h-3" />
          {modo === "caja" ? "Solo pedidos ya cobrados" : "Todos los pedidos confirmados"}
        </div>
        <ExcelExportButton
          endpoint="/reportes/rentabilidad/excel"
          filename="rentabilidad"
          label="Exportar Excel"
          extraParams={{ modo }}
          className="h-9 ml-auto"
          testid="btn-exportar-rentabilidad"
        />
      </div>

      {loading ? (
        <div className="p-10 text-center text-muted-foreground">Cargando…</div>
      ) : (
        <>
          {/* Utilidad neta prominente */}
          <div
            className="rounded-2xl border-2 border-primary/40 bg-gradient-to-br from-primary/8 via-card to-emerald-500/5 p-6 mb-6"
            data-testid="utilidad-neta-card"
            title={modo === "caja"
              ? "Utilidad neta calculada solo con los pedidos ya cobrados al 100%."
              : "Utilidad neta calculada con todos los pedidos confirmados del periodo, hayan sido cobrados o no."}
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary/70 mb-1">Utilidad neta del periodo</div>
                <div className="text-5xl md:text-6xl font-bold tabular-nums text-foreground" data-testid="utilidad-neta-monto">
                  {formatCOP(kpis.utilidad_neta)}
                </div>
                <div className="text-sm text-muted-foreground mt-2 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <Percent className="w-3.5 h-3.5" /> Margen neto {kpis.margen_neto_pct?.toFixed(1)}%
                  </span>
                  <span>·</span>
                  <span>{kpis.pedidos} pedidos</span>
                  {cmp && (
                    <>
                      <span>·</span>
                      <span className={`inline-flex items-center gap-1 font-semibold ${cmp.delta >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                        {cmp.delta >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                        {cmp.delta >= 0 ? "+" : ""}{formatCOP(cmp.delta)} vs {formatMesLabel(cmp.prev.mes)}
                        {cmp.pct !== null && (
                          <span className="opacity-70">({cmp.pct >= 0 ? "+" : ""}{cmp.pct.toFixed(1)}%)</span>
                        )}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">De cada $100 vendidos</div>
                <div className="text-2xl font-semibold text-primary tabular-nums">
                  ${(kpis.margen_neto_pct || 0).toFixed(1)}
                </div>
                <div className="text-[10px] text-muted-foreground">quedan limpios</div>
              </div>
            </div>
          </div>

          {/* Cascada de KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <FlowCard
              label="Ventas" to={(() => { const r = rangoEfectivo(); return `/productos?tab=historial${r.desde ? `&desde=${r.desde}&hasta=${r.hasta}` : ""}`; })()}
              value={formatCOP(kpis.ventas)}
              icon={ShoppingCart}
              tone="blue"
              sub={`${kpis.pedidos} pedidos`}
              testid="kpi-ventas"
              tooltip={modo === "caja"
                ? "Total facturado de los pedidos que el cliente ya pagó al 100% en el periodo."
                : "Total facturado de todos los pedidos confirmados/despachados/entregados en el periodo seleccionado, con descuentos aplicados. Ojo: el Panel principal muestra los últimos 30 días corridos; aquí ves el rango elegido arriba (por defecto el mes calendario), por eso pueden diferir un poco."}
            />
            <FlowCard
              label="− Costo mercancía" to="/productos?tab=inventario"
              value={formatCOP(kpis.cogs)}
              icon={Package}
              tone="amber"
              sub={`Bruto: ${kpis.margen_bruto_pct?.toFixed(1)}%`}
              testid="kpi-cogs"
              tooltip="Costo de la mercancía vendida. Si el pedido ya se despachó, usa el costo real del lote FIFO consumido; si no, el costo congelado en el ítem al crear el pedido. Incluye el landed cost del contenedor (flete + nacionalización + gastos). Si el % bruto se ve bajo para tus márgenes, abre 'Revisión de costos' más abajo."
            />
            <FlowCard
              label="− Comisiones" to="/finanzas?tab=comisiones"
              value={formatCOP(kpis.comisiones)}
              icon={Award}
              tone="rose"
              sub={kpis.ventas ? `${((kpis.comisiones / kpis.ventas) * 100).toFixed(1)}% de ventas` : ""}
              testid="kpi-comisiones"
              tooltip="Comisión total causada por los pedidos del periodo, con el % congelado al momento de la venta."
            />
            <FlowCard
              label="− Otros gastos" to="/finanzas?tab=gastos"
              value={formatCOP(kpis.gastos)}
              icon={Wallet}
              tone="muted"
              sub={mesesEnRango.length > 0 ? `${mesesEnRango.length} mes(es)` : "Sin registro"}
              testid="kpi-gastos"
              tooltip="Suma de gastos operativos (nómina, arriendo, servicios, transporte…) registrados para los meses del periodo."
              action={
                (mesesEnRango.length > 0 || periodo.modo === "mes") ? (
                  <button
                    onClick={() => abrirEditGasto(periodo.modo === "mes" ? periodo.mes : mesesEnRango[mesesEnRango.length - 1])}
                    className="text-[10px] font-semibold text-primary hover:underline inline-flex items-center gap-1"
                    data-testid="btn-editar-gasto-actual"
                    title="Agregar o revisar los gastos del mes"
                  >
                    <Pencil className="w-3 h-3" /> Agregar gastos
                  </button>
                ) : null
              }
            />
          </div>

          {/* Gráfico + tabla vendedor lado a lado */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* Chart */}
            <div className="lg:col-span-2 bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-semibold text-foreground">Utilidad mensual · últimos 6 meses</div>
                  <div className="text-xs text-muted-foreground">Barras apiladas: costo + comisiones + gastos vs utilidad neta</div>
                </div>
              </div>
              <div className="h-72" data-testid="chart-utilidad-mensual">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={serie} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis
                      dataKey="mes"
                      tickFormatter={formatMesLabel}
                      tick={{ fontSize: 12 }}
                      stroke="rgba(0,0,0,0.5)"
                    />
                    <YAxis
                      tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`}
                      tick={{ fontSize: 11 }}
                      stroke="rgba(0,0,0,0.5)"
                    />
                    <Tooltip
                      formatter={(v, name) => [formatCOP(v), name]}
                      labelFormatter={formatMesLabel}
                      contentStyle={{ borderRadius: 8, fontSize: 12 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="cogs" name="Costo mercancía" stackId="a" fill="#f59e0b" />
                    <Bar dataKey="comisiones" name="Comisiones" stackId="a" fill="#ec4899" />
                    <Bar dataKey="gastos" name="Otros gastos" stackId="a" fill="#94a3b8" />
                    <Bar dataKey="utilidad_neta" name="Utilidad neta" stackId="a" fill="#10b981" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">
                Nota: si un mes no tiene gasto registrado, se muestra en cero. Registra los gastos desde la tabla mensual abajo.
              </div>
            </div>

            {/* Tabla vendedores */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border/60">
                <div className="text-sm font-semibold text-foreground">Utilidad por vendedor</div>
                <div className="text-xs text-muted-foreground">Antes de descontar gastos operativos</div>
              </div>
              {vendedores.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">Sin ventas en el periodo</div>
              ) : (
                <div className="divide-y divide-border/60">
                  {vendedores.map((v) => (
                    <div key={v.vendedor_id} className="p-3" data-testid={`vendedor-row-${v.vendedor_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-sm text-foreground truncate">{v.vendedor_nombre}</div>
                        <div className="text-[10px] text-muted-foreground shrink-0">{v.participacion_pct}% del total</div>
                      </div>
                      <div className="text-lg font-semibold tabular-nums text-emerald-700 mt-0.5">
                        {formatCOP(v.utilidad_pre_gastos)}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                        <span>Ventas {formatCOP(v.ventas)}</span>
                        <span>·</span>
                        <span>{v.pedidos} pedidos</span>
                        <span>·</span>
                        <span>Bruto {v.margen_bruto_pct}%</span>
                      </div>
                      <div className="mt-1.5 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-primary to-emerald-500"
                          style={{ width: `${Math.max(3, Math.min(100, v.participacion_pct || 0))}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Jun/2026: auditoría de costos (COGS inflado por snapshots viejos / paquete vs sub-paquete) */}
          <RevisionCostosPanel desde={rangoEfectivo().desde} hasta={rangoEfectivo().hasta} modo={modo} onRecalculado={load} />

          {/* Nota: el detalle de otros gastos mensuales se administra en la pestaña Gastos mensuales */}
        </>
      )}

      {gastoOpen && (
        <GastoModal
          gasto={gastoEdit}
          onClose={(saved) => {
            setGastoOpen(false); setGastoEdit(null);
            if (saved) { load(); loadGastos(); }
          }}
        />
      )}
    </div>
  );
}

function FlowCard(props) { return <KpiCard {...props} />; }

function GastoModal({ gasto, onClose }) {
  // Jun/2026: gastos itemizados — el director agrega tantos gastos como quiera
  // en el mes (arriendo, nómina, servicios…) y se van sumando.
  const [mes, setMes] = useState(gasto?.mes || mesISOLocal());
  const [lista, setLista] = useState([]);
  const [transporte, setTransporte] = useState(0);
  const [concepto, setConcepto] = useState("");
  const [monto, setMonto] = useState("");
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [cambio, setCambio] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const { data } = await api.get(`/gastos-mensuales`, { params: { mes } });
      setLista(data?.mes_actual?.gastos_manuales || []);
      setTransporte(Number(data?.mes_actual?.conceptos?.transporte_pedidos || 0));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar los gastos");
    } finally { setCargando(false); }
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [mes]);

  const agregar = async () => {
    const m = parseInt(monto, 10);
    if (!concepto.trim()) { toast.error("Escribe el concepto del gasto"); return; }
    if (!m || m <= 0) { toast.error("El monto debe ser mayor a 0"); return; }
    setSaving(true);
    try {
      await api.post(`/gastos-mensuales/manual`, { mes, concepto: concepto.trim(), monto: m, notas: notas || null });
      toast.success(`Gasto agregado a ${mes}`);
      setConcepto(""); setMonto(""); setNotas("");
      setCambio(true);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este gasto?")) return;
    try {
      await api.delete(`/gastos-mensuales/manual/${id}`, { params: { mes } });
      setCambio(true);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const totalManual = lista.reduce((a, g) => a + Number(g.monto || 0), 0);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(cambio); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="gasto-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2 flex-wrap">
            <Wallet className="w-5 h-5 text-primary" /> Gastos del mes
            <input type="month" value={mes} onChange={(e) => e.target.value && setMes(e.target.value)} className="ml-auto h-9 px-2 rounded-md border border-border bg-background text-sm font-sans" data-testid="gasto-mes-input" />
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Agrega cada gasto por separado (nómina, arriendo, servicios, marketing…). Se suman automáticamente al total del mes.
          </p>
        </DialogHeader>

        <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-2" data-testid="gasto-form">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-2">
            <div>
              <Label htmlFor="gasto-concepto">Concepto</Label>
              <Input id="gasto-concepto" value={concepto} onChange={(e) => setConcepto(e.target.value)} placeholder="Ej: Arriendo bodega" className="h-11" data-testid="input-gasto-concepto" autoFocus />
            </div>
            <div>
              <Label htmlFor="gasto-monto">Monto (COP)</Label>
              <Input id="gasto-monto" type="number" min="0" value={monto} onChange={(e) => setMonto(e.target.value)} className="h-11 tabular-nums" data-testid="input-gasto-monto" onKeyDown={(e) => { if (e.key === "Enter") agregar(); }} />
            </div>
          </div>
          <div>
            <Label htmlFor="gasto-notas">Notas (opcional)</Label>
            <Input id="gasto-notas" value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Detalle breve" className="h-10" data-testid="input-gasto-notas" />
          </div>
          <Button onClick={agregar} disabled={saving} className="bg-primary w-full h-11" data-testid="btn-agregar-gasto">
            {saving ? "Guardando…" : "+ Agregar gasto"}
          </Button>
        </div>

        <div className="mt-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Gastos registrados en {mes}</div>
          {cargando ? (
            <div className="text-sm text-muted-foreground py-4 text-center">Cargando…</div>
          ) : lista.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-xl" data-testid="gastos-vacio">Aún no hay gastos manuales este mes.</div>
          ) : (
            <div className="divide-y divide-border border border-border rounded-xl overflow-hidden" data-testid="gastos-lista">
              {lista.map((g) => (
                <div key={g.id} className="flex items-center gap-3 px-3 py-2" data-testid={`gasto-item-${g.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground truncate">{g.concepto}</div>
                    {g.notas && <div className="text-[11px] text-muted-foreground truncate">{g.notas}</div>}
                  </div>
                  <div className="text-sm font-semibold tabular-nums">{formatCOP(g.monto)}</div>
                  <button onClick={() => eliminar(g.id)} className="text-muted-foreground hover:text-rose-600 text-xs px-1" title="Eliminar" data-testid={`gasto-eliminar-${g.id}`}>✕</button>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 space-y-1 text-sm">
            {transporte > 0 && (
              <div className="flex justify-between text-muted-foreground"><span>Transporte asumido por Beem (automático)</span><span className="tabular-nums">{formatCOP(transporte)}</span></div>
            )}
            <div className="flex justify-between font-semibold text-foreground" data-testid="gasto-total-mes"><span>Total gastos del mes</span><span className="tabular-nums">{formatCOP(totalManual + transporte)}</span></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose(cambio)} data-testid="btn-cerrar-gasto">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
